import { extendTheme } from '@chakra-ui/react'

const colors = {
    brand: {
        primary: '#1C1C1C', // Black
        white: '#FFFFFF',
        green: '#0BDD4D',
        accent: '#00FF00',
        cream: {
            soft: '#FAFAF6',
            base: '#F3F4E9',
        },

        // Secondary Colors 2024
        secondary: {
            pink: '#FEA5FB',
            blue: '#B3BBFF',
            orange: '#FFCB7D',
            yellow: '#FFFB94',
            green: '#6AFA97',
        },

        // Secondary Colors - Darker Tones
        dark: {
            pink: '#F650F0',
            blue: '#011AFB',
            orange: '#FF9900',
            yellow: '#FAF000',
            olive: '#447216',
        },

        // Color Tints
        tints: {
            green: {
                100: '#0BDD4D',
                75: '#48E578',
                55: '#79EC9D',
                30: '#B6F5CA',
            },
            pink: {
                100: '#F650F0',
                75: '#F87CF4',
                55: '#FA9FF7',
                30: '#FCCBFB',
            },
            blue: {
                100: '#011AFB',
                75: '#4053FC',
                55: '#7381FD',
                30: '#B3BAFE',
            },
            orange: {
                100: '#FF9900',
                75: '#FFB340',
                55: '#FFC773',
                30: '#FFE0B3',
            },
            yellow: {
                100: '#FAF000',
                75: '#FBF440',
                55: '#FCF773',
                30: '#FEFBB3',
            },
            amber: {
                100: '#FF9900',
                75: '#FFB340',
                55: '#FFC773',
                30: '#FFE0B3',
            },
            olive: {
                100: '#447216',
                75: '#739550',
                55: '#98B17F',
                30: '#C7D5B9',
            },
        },

        background: {
            primary: '#1C1C1C',
            secondary: '#F8F8F8',
            tertiary: '#FAFAFA',
            hover: 'rgba(28, 28, 28, 0.04)',
            selected: 'rgba(28, 28, 28, 0.08)',
            disabled: 'rgba(28, 28, 28, 0.12)',
        },
        status: {
            active: 'rgba(106, 250, 151, 0.3)',
            activeBase: '#6AFA97',
            complete: '#FFE8FF',
            success: {
                background: 'rgba(106, 250, 151, 0.1)',
                border: '#6AFA97',
                text: '#1C1C1C',
            },
            error: {
                background: 'rgba(255, 59, 48, 0.1)',
                border: '#FF3B30',
                text: '#1C1C1C',
            },
            warning: {
                background: 'rgba(255, 149, 0, 0.1)',
                border: '#FF9500',
                text: '#1C1C1C',
            },
            info: {
                background: 'rgba(0, 122, 255, 0.1)',
                border: '#007AFF',
                text: '#1C1C1C',
            },
        },
        ui: {
            border: '#E0E0E0',
            borderHover: '#E0E0E0',
            hover: '#E0E0E0',
            focus: '#E0E0E0',
            borderFocus: '#E0E0E0',
            shadow: 'rgba(0, 0, 0, 0.1)',
            text: {
                primary: '#1C1C1C',
                secondary: '#757575',
                disabled: '#BDBDBD',
                inverse: '#FFFFFF',
            },
        },
    },
    // Preserve existing color schemes
    blue: {
        50: '#cbe3ff',
        100: '#beddff',
        200: '#97c8ff',
        300: '#6fb3ff',
        400: '#489fff',
        500: '#0a84ff',
        600: '#0063d2',
        700: '#0051aa',
        800: '#003e83',
        900: '#00356f',
    },
    green: {
        50: '#b0e2a9',
        100: '#a0dc98',
        200: '#90d687',
        300: '#80d076',
        400: '#70ca65',
        500: '#34c759',
        600: '#57b14b',
        700: '#4d9d43',
        800: '#43893a',
        900: '#3a7632',
    },
}

const typography = {
    fonts: {
        body: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
        heading: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`,
    },
    fontSizes: {
        xs: '12px',
        sm: '14px',
        md: '16px',
        lg: '20px',
        xl: '24px',
        '2xl': '32px',
        '3xl': '40px',
    },
    fontWeights: {
        normal: 400,
        medium: 500,
        bold: 700,
    },
    lineHeights: {
        none: 1,
        tight: 1.25,
        base: 1.5,
        relaxed: 1.75,
    },
    letterSpacings: {
        tighter: '-0.05em',
        tight: '-0.025em',
        normal: '0',
        wide: '0.025em',
        wider: '0.05em',
    },
    textStyles: {
        link: {
            color: 'blue.500',
            fontWeight: 'medium',
            marginBottom: 2,
        },
    },
}

const space = {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    layoutGutter: '24px',
    sectionSpacing: '48px',
    componentSpacing: '24px',
    relatedItemSpacing: '16px',
}

const radii = {
    none: '0',
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    '2xl': '24px',
    full: '9999px',
}

const borders = {
    none: 'none',
    thin: '1px solid',
    medium: '2px solid',
    thick: '4px solid',
    variants: {
        default: {
            borderWidth: 'thin',
            borderStyle: 'solid',
            borderColor: 'brand.ui.border',
            borderRadius: 'md',
        },
        featured: {
            borderWidth: 'medium',
            borderStyle: 'solid',
            borderColor: 'brand.status.activeBase',
            borderRadius: 'lg',
        },
        interactive: {
            borderWidth: 'thin',
            borderStyle: 'solid',
            borderColor: 'brand.ui.border',
            borderRadius: 'md',
            _hover: {
                borderColor: 'brand.status.activeBase',
                borderWidth: 'medium',
            },
        },
        dashed: {
            borderWidth: 'medium',
            borderStyle: 'dashed',
            borderColor: 'brand.ui.border',
            borderRadius: 'md',
            _hover: {
                borderColor: 'brand.status.activeBase',
            },
        },
        success: {
            borderWidth: 'medium',
            borderStyle: 'solid',
            borderColor: 'brand.status.success.border',
            borderRadius: 'md',
        },
        error: {
            borderWidth: 'medium',
            borderStyle: 'solid',
            borderColor: 'brand.status.error.border',
            borderRadius: 'md',
        },
        warning: {
            borderWidth: 'medium',
            borderStyle: 'solid',
            borderColor: 'brand.status.warning.border',
            borderRadius: 'md',
        },
        info: {
            borderWidth: 'medium',
            borderStyle: 'solid',
            borderColor: 'brand.status.info.border',
            borderRadius: 'md',
        },
    },
}

const elevation = {
    shadows: {
        xs: '0 1px 2px rgba(0, 0, 0, 0.05)',
        sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
        md: '0 4px 6px rgba(0, 0, 0, 0.1)',
        lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
        xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
        '2xl': '0 25px 50px rgba(0, 0, 0, 0.25)',
        inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    layerStyles: {
        base: {
            bg: 'brand.white',
            transition: 'box-shadow 0.2s ease-out, transform 0.2s ease-out',
        },
        flat: {
            bg: 'brand.white',
            border: '1px solid',
            borderColor: 'brand.ui.border',
        },
        raised: {
            bg: 'brand.white',
            boxShadow: 'sm',
            _hover: {
                boxShadow: 'md',
            },
        },
        floating: {
            bg: 'brand.white',
            boxShadow: 'lg',
            _hover: {
                transform: 'translateY(-2px)',
                boxShadow: 'xl',
            },
        },
        sticky: {
            bg: 'brand.white',
            boxShadow: 'md',
            position: 'sticky',
            top: 0,
            zIndex: 'sticky',
        },
        modal: {
            bg: 'brand.white',
            boxShadow: 'xl',
            borderRadius: 'xl',
        },
        popover: {
            bg: 'brand.white',
            boxShadow: 'lg',
            borderRadius: 'md',
        },
        dropdown: {
            bg: 'brand.white',
            boxShadow: 'md',
            borderRadius: 'md',
            border: '1px solid',
            borderColor: 'brand.ui.border',
        },
    },
}

const zIndices = {
    hide: -1,
    base: 0,
    docked: 10,
    dropdown: 1000,
    sticky: 1100,
    banner: 1200,
    overlay: 1300,
    modal: 1400,
    popover: 1500,
    skipLink: 1600,
    toast: 1700,
    tooltip: 1800,
}

const transitions = {
    duration: {
        fast: '150ms',
        normal: '200ms',
        slow: '300ms',
        slower: '400ms',
    },
    timing: {
        'ease-out': 'ease-out',
        'ease-in-out': 'ease-in-out',
        'ease-in': 'ease-in',
    },
    properties: {
        common: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
        colors: 'background-color, border-color, color, fill, stroke',
        dimensions: 'height, width',
        position: 'left, right, top, bottom',
        background: 'background-color, background-image, background-position',
    },
}

const components = {
    Button: {
        baseStyle: {
            fontWeight: 'medium',
            borderRadius: 'md',
            transition: 'all 0.2s ease-out',
        },
        variants: {
            primary: {
                bg: 'brand.status.activeBase',
                color: 'brand.primary',
                _hover: {
                    bg: 'rgba(106, 250, 151, 0.8)',
                    _disabled: {
                        bg: 'rgba(106, 250, 151, 0.4)',
                    },
                },
                _active: {
                    transform: 'scale(0.98)',
                },
            },
            secondary: {
                bg: 'brand.primary',
                color: 'brand.white',
                border: '1px solid',
                borderColor: 'brand.white',
                _hover: {
                    bg: 'rgba(28, 28, 28, 0.8)',
                    borderColor: 'rgba(233, 232, 232, 0.8)',
                    _disabled: {
                        bg: 'rgba(28, 28, 28, 0.4)',
                    },
                },
                _active: {
                    transform: 'scale(0.98)',
                },
            },
            ghost: {
                bg: 'transparent',
                color: 'brand.primary',
                border: '1px solid',
                borderColor: 'brand.primary',
                _hover: {
                    bg: 'brand.background.hover',
                    borderColor: 'brand.ui.borderHover',
                },
                _active: {
                    bg: 'brand.background.selected',
                    borderColor: 'brand.primary',
                },
            },
            search: {
                bg: 'transparent',
                color: 'brand.ui.text.primary',
                border: '1px solid',
                borderColor: 'brand.ui.border',
                _hover: {
                    bg: 'brand.background.hover',
                    borderColor: 'brand.ui.borderHover',
                },
                _active: {
                    bg: 'brand.background.selected',
                    borderColor: 'brand.primary',
                },
            },
            noBorder: {
                bg: 'transparent',
                color: 'brand.primary',
                border: 'none',
                _hover: {
                    bg: 'brand.background.hover',
                    borderColor: 'brand.ui.borderHover',
                },
                _active: {
                    bg: 'brand.background.selected',
                    borderColor: 'brand.primary',
                },
            },
            success: {
                bg: 'brand.status.success.background',
                color: 'brand.status.success.text',
                border: '1px solid',
                borderColor: 'brand.status.success.border',
            },
            error: {
                bg: 'brand.status.error.background',
                color: 'brand.status.error.text',
                border: '1px solid',
                borderColor: 'brand.status.error.border',
            },
            warning: {
                bg: 'brand.status.warning.background',
                color: 'brand.status.warning.text',
                border: '1px solid',
                borderColor: 'brand.status.warning.border',
            },
            info: {
                bg: 'brand.status.info.background',
                color: 'brand.status.info.text',
                border: '1px solid',
                borderColor: 'brand.status.info.border',
            },
        },
        sizes: {
            sm: {
                fontSize: 'sm',
                px: 4,
                py: 2,
                height: '32px',
                minWidth: '80px',
            },
            md: {
                fontSize: 'md',
                px: 6,
                py: 3,
                height: '40px',
                minWidth: '100px',
            },
            lg: {
                fontSize: 'lg',
                px: 8,
                py: 4,
                height: '48px',
                minWidth: '120px',
            },
        },
        defaultProps: {
            variant: 'primary',
            size: 'md',
        },
    },
    Card: {
        baseStyle: {
            ...elevation.layerStyles.base,
            bg: 'brand.white',
            borderRadius: 'lg',
            p: 'md',
            boxShadow: 'sm',
            transition: 'box-shadow 0.2s ease-out',
            _hover: {
                boxShadow: 'md',
            },
        },
        variants: {
            flat: {
                ...elevation.layerStyles.flat,
            },
            raised: {
                ...elevation.layerStyles.raised,
            },
            floating: {
                ...elevation.layerStyles.floating,
            },
            sticky: {
                ...elevation.layerStyles.sticky,
            },
        },
        defaultProps: {
            variant: 'raised',
        },
    },
    Badge: {
        baseStyle: {
            borderRadius: '16px',
            px: 2,
            py: 1,
            fontSize: 'xs',
            fontWeight: 'medium',
        },
        variants: {
            active: {
                bg: 'brand.status.active',
                color: 'brand.primary',
            },
            complete: {
                bg: 'brand.status.complete',
                color: 'brand.primary',
            },
            external: {
                bg: 'brand.tints.pink.30',
                color: 'brand.primary',
            },
        },
    },
    Link: {
        baseStyle: {
            transition: 'opacity 0.15s ease-in-out',
            _hover: {
                opacity: 0.8,
                textDecoration: 'none',
            },
        },
    },
    Input: {
        variants: {
            outline: {
                field: {
                    borderColor: 'brand.ui.border',
                    _hover: {
                        borderColor: 'brand.ui.borderHover',
                    },
                    _focus: {
                        borderColor: 'brand.ui.borderFocus',
                        boxShadow: '0 0 0 1px #E0E0E0',
                    },
                },
            },
        },
    },
    Modal: {
        baseStyle: {
            overlay: {
                bg: 'blackAlpha.300',
                backdropFilter: 'blur(8px)',
            },
            dialog: {
                ...elevation.layerStyles.modal,
                bg: 'brand.white',
                boxShadow: 'xl',
                borderRadius: 'xl',
                mx: 4,
                overflow: 'hidden',
            },
            header: {
                px: 6,
                py: 4,
                fontSize: 'xl',
                fontWeight: 'medium',
                borderBottom: '1px solid',
                borderColor: 'brand.ui.border',
            },
            body: {
                px: 6,
                py: 4,
            },
            footer: {
                px: 6,
                py: 4,
                borderTop: '1px solid',
                borderColor: 'brand.ui.border',
            },
            closeButton: {
                position: 'absolute',
                right: 4,
                top: 4,
                variant: 'ghost',
                size: 'sm',
                _hover: { bg: 'brand.background.hover' },
            },
        },
        sizes: {
            xs: {
                dialog: { maxW: '320px' },
            },
            sm: {
                dialog: { maxW: '384px' },
            },
            md: {
                dialog: { maxW: '448px' },
            },
            lg: {
                dialog: { maxW: '768px' },
            },
            xl: {
                dialog: { maxW: '1100px' },
            },
            full: {
                dialog: { maxW: '100vw', minH: '100vh', m: 0 },
            },
        },
        defaultProps: {
            size: 'md',
        },
        variants: {
            confirmation: {
                dialog: {
                    maxW: '384px',
                },
                header: {
                    borderBottom: 'none',
                    pb: 0,
                },
                footer: {
                    borderTop: 'none',
                    pt: 0,
                },
            },
            fullscreen: {
                dialog: {
                    maxW: '100vw',
                    minH: '100vh',
                    m: 0,
                    borderRadius: 0,
                },
            },
            drawer: {
                dialog: {
                    h: '100vh',
                    maxW: '600px',
                    m: 0,
                    ml: 'auto',
                    borderRadius: 0,
                },
            },
        },
    },
    Popover: {
        baseStyle: {
            content: {
                ...elevation.layerStyles.popover,
            },
        },
    },
    Menu: {
        baseStyle: {
            list: {
                ...elevation.layerStyles.dropdown,
            },
        },
    },
}

const breakpoints = {
    sm: '320px',
    md: '768px',
    lg: '1024px',
    xl: '1200px',
}

const styles = {
    global: {
        body: {
            bg: 'brand.background.primary',
            color: 'brand.ui.text.primary',
            fontSize: 'sm',
            lineHeight: 'base',
        },
        '*': {
            borderColor: 'brand.ui.border',
            borderStyle: 'solid',
        },
        'h1, h2, h3, h4, h5, h6': {
            fontFamily: 'heading',
            fontWeight: 'bold',
            color: 'brand.ui.text.primary',
        },
        p: {
            color: 'brand.ui.text.primary',
            lineHeight: 'base',
        },
        a: {
            color: 'brand.status.activeBase',
            _hover: {
                textDecoration: 'none',
                opacity: 0.8,
            },
        },
        'main, [role="main"]': {
            bg: 'brand.white',
            color: 'brand.ui.text.primary',
        },
        'nav, [role="navigation"]': {
            color: 'brand.white',
        },
    },
}

const layoutStyles = {
    // Page layouts
    page: {
        base: {
            maxW: 'container.xl',
            px: 'layoutGutter',
            mx: 'auto',
            width: '100%',
        },
        withSidebar: {
            display: 'grid',
            gridTemplateColumns: {
                base: '1fr',
                md: '250px 1fr',
                lg: '280px 1fr',
            },
            gap: 'componentSpacing',
        },
        withAside: {
            display: 'grid',
            gridTemplateColumns: {
                base: '1fr',
                md: '1fr 320px',
                lg: '1fr 380px',
            },
            gap: 'componentSpacing',
        },
    },
    // Section layouts
    section: {
        base: {
            width: '100%',
            py: 'sectionSpacing',
        },
        card: {
            width: '100%',
            p: 6,
            borderRadius: 'md',
            bg: 'white',
            boxShadow: 'sm',
        },
        form: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'componentSpacing',
        },
    },
    // Grid layouts
    grid: {
        base: {
            display: 'grid',
            gap: 'componentSpacing',
        },
        responsive2: {
            gridTemplateColumns: {
                base: '1fr',
                md: 'repeat(2, 1fr)',
            },
        },
        responsive3: {
            gridTemplateColumns: {
                base: '1fr',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(3, 1fr)',
            },
        },
        responsive4: {
            gridTemplateColumns: {
                base: '1fr',
                md: 'repeat(2, 1fr)',
                lg: 'repeat(4, 1fr)',
            },
        },
    },
    // List layouts
    list: {
        base: {
            display: 'flex',
            flexDirection: 'column',
            gap: 'relatedItemSpacing',
        },
        horizontal: {
            display: 'flex',
            flexDirection: { base: 'column', md: 'row' },
            gap: 'relatedItemSpacing',
        },
    },
}

const theme = extendTheme({
    colors,
    ...typography,
    space,
    radii,
    borders,
    shadows: elevation.shadows,
    layerStyles: elevation.layerStyles,
    zIndices,
    transitions,
    breakpoints,
    components,
    styles,
    layoutStyles,
    sizes: {
        container: {
            sm: '640px',
            md: '768px',
            lg: '1024px',
            xl: '1200px',
        },
    },
    icons: {
        sizes: {
            default: '20px',
            action: '16px',
        },
        strokeWidth: '1.5px',
    },
})

export default theme
